const TIMEZONE = 'Asia/Jerusalem';
const DEFAULT_DURATION_MINUTES = 30;

function pad(n) {
  return String(n).padStart(2, '0');
}

function parseBookingParts(date, time) {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return { year, month, day, hour, minute };
}

function addMinutes(date, time, minutes) {
  const { year, month, day, hour, minute } = parseBookingParts(date, time);
  const start = new Date(year, month - 1, day, hour, minute, 0);
  const end = new Date(start.getTime() + minutes * 60 * 1000);
  return {
    date: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
    time: `${pad(end.getHours())}:${pad(end.getMinutes())}`,
  };
}

function toIcsLocal(date, time) {
  const { year, month, day, hour, minute } = parseBookingParts(date, time);
  return `${year}${pad(month)}${pad(day)}T${pad(hour)}${pad(minute)}00`;
}

function toGoogleDate(date, time) {
  return toIcsLocal(date, time);
}

function escapeIcsText(text) {
  return String(text || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function utcStampNow() {
  const now = new Date();
  return (
    `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}`
    + `T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`
  );
}

/**
 * Build a calendar event object from booking fields.
 */
export function buildBookingCalendarEvent({
  id,
  date,
  time,
  bookingType,
  salonTitle,
  salonAddress,
  durationMinutes = DEFAULT_DURATION_MINUTES,
}) {
  const service = bookingType || 'حجز';
  const salon = salonTitle || 'صالون';
  const end = addMinutes(date, time, durationMinutes);

  return {
    id,
    title: `${service} — ${salon}`,
    description: `موعد ${service} في ${salon}.\nمحجوز عبر ادواري`,
    location: salonAddress || '',
    startDate: date,
    startTime: time,
    endDate: end.date,
    endTime: end.time,
    timezone: TIMEZONE,
  };
}

/**
 * Download a .ics file (Apple Calendar, Outlook, etc.).
 */
export function downloadBookingIcs(event) {
  const uid = event.id ? `booking-${event.id}@adwary.app` : `booking-${Date.now()}@adwary.app`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Adwary//Salon Booking//AR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${utcStampNow()}`,
    `DTSTART;TZID=${event.timezone}:${toIcsLocal(event.startDate, event.startTime)}`,
    `DTEND;TZID=${event.timezone}:${toIcsLocal(event.endDate, event.endTime)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
  ];

  if (event.location) {
    lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `adwary-${event.startDate}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Open Google Calendar with a pre-filled event.
 */
export function openGoogleCalendar(event) {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toGoogleDate(event.startDate, event.startTime)}/${toGoogleDate(event.endDate, event.endTime)}`,
    details: event.description,
    ctz: event.timezone,
  });

  if (event.location) {
    params.set('location', event.location);
  }

  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer');
}

export function bookingToCalendarEvent(booking, extras = {}) {
  return buildBookingCalendarEvent({
    id: booking.id,
    date: booking.date,
    time: booking.time,
    bookingType: booking.booking_type,
    salonTitle: booking.account_title || extras.salonTitle,
    salonAddress: booking.account_address || extras.salonAddress,
    durationMinutes: booking.duration_minutes || extras.durationMinutes,
  });
}
